/**
 * Practice finances: dashboard, invoices/payments (Stripe), AR, AP.
 * First draft with mock data; wire to Stripe and backend later.
 */

import {
  Badge,
  Card,
  Container,
  Group,
  Paper,
  Pagination,
  SegmentedControl,
  Skeleton,
  Stack,
  Table,
  Tabs,
  Text,
  Title,
} from '@mantine/core';
import { ChartCard } from '../../components/ChartCard';
import { UnifyLineChart } from '../../components/charts';
import { LocalhostOnlyBadge } from '../../components/LocalhostOnlyBadge';
import {
  IconArrowUpRight,
  IconCash,
  IconCreditCard,
  IconReceipt,
  IconTrendingUp,
  IconWallet,
} from '@tabler/icons-react';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

const TAB_PARAM_DASHBOARD = 'dashboard';
const TAB_PARAM_INVOICE = 'invoice';
const TAB_PARAM_PAYMENTS = 'payments';

const MONTHLY_TREND_DATA = [
  { period: 'Aug', revenue: 42000 },
  { period: 'Sep', revenue: 45800 },
  { period: 'Oct', revenue: 44100 },
  { period: 'Nov', revenue: 48900 },
  { period: 'Dec', revenue: 52100 },
  { period: 'Jan', revenue: 54700 },
];

const WEEKLY_TREND_DATA = [
  { period: 'Aug 5', revenue: 9800 },
  { period: 'Aug 12', revenue: 10500 },
  { period: 'Aug 19', revenue: 10200 },
  { period: 'Aug 26', revenue: 11500 },
  { period: 'Sep 2', revenue: 10800 },
  { period: 'Sep 9', revenue: 11200 },
  { period: 'Sep 16', revenue: 11600 },
  { period: 'Sep 23', revenue: 12200 },
  { period: 'Sep 30', revenue: 10400 },
  { period: 'Oct 7', revenue: 11000 },
  { period: 'Oct 14', revenue: 10600 },
  { period: 'Oct 21', revenue: 12100 },
  { period: 'Oct 28', revenue: 11800 },
  { period: 'Nov 4', revenue: 11500 },
  { period: 'Nov 11', revenue: 12300 },
  { period: 'Nov 18', revenue: 12800 },
  { period: 'Nov 25', revenue: 12300 },
  { period: 'Dec 2', revenue: 12600 },
  { period: 'Dec 9', revenue: 13100 },
  { period: 'Dec 16', revenue: 13400 },
  { period: 'Dec 23', revenue: 13000 },
  { period: 'Dec 30', revenue: 13600 },
  { period: 'Jan 6', revenue: 13200 },
  { period: 'Jan 13', revenue: 13900 },
  { period: 'Jan 20', revenue: 13800 },
  { period: 'Jan 27', revenue: 13800 },
];

export interface SessionLineItem {
  id: string;
  date: string;
  time: string;
  client: string;
  provider: string;
  cptCode: string;
  duration: number;
  rateType: 'Licensed' | 'Pre-Licensed';
  amount: number;
  /** Shown in Adjustments table when present */
  reason?: string;
}

export interface Invoice {
  id: string;
  date: string;
  payPeriod: string;
  amount: number;
  status: 'Paid' | 'Pending' | 'Overdue';
  stripeId: string;
  bankAccount: string;
  sessions: SessionLineItem[];
  adjustments: SessionLineItem[];
}

function generateMockSessions(payPeriodStart: string, count: number): SessionLineItem[] {
  const clients = [
    'Smith, J.', 'Garcia, M.', 'Johnson, T.', 'Williams, R.', 'Brown, A.',
    'Davis, K.', 'Wilson, S.', 'Miller, L.', 'Taylor, D.', 'Anderson, P.',
    'Thomas, C.', 'Jackson, B.', 'White, E.', 'Harris, N.', 'Martin, F.',
    'Thompson, G.', 'Moore, H.', 'Clark, I.', 'Lewis, J.', 'Robinson, K.',
  ];
  const providers = [
    'Dr. Sarah Chen', 'Dr. Michael Ross', 'Dr. Emily Park', 'Dr. James Liu', 'Dr. Laura Kim',
  ];
  const cptCodes = ['90834', '90837', '90847', '90832', '90853'];
  const durations = [30, 45, 53, 60];
  const amounts = [125, 150, 175, 200, 225];
  const times = ['9:00 AM', '9:30 AM', '10:00 AM', '11:00 AM', '2:00 PM', '2:30 PM', '3:00 PM', '4:00 PM', '5:00 PM'];
  const start = new Date(payPeriodStart);
  const sessions: SessionLineItem[] = [];
  for (let i = 0; i < count; i++) {
    const dayOffset = Math.floor(Math.random() * 14);
    const sessionDate = new Date(start);
    sessionDate.setDate(start.getDate() + dayOffset);
    sessions.push({
      id: `sess-${payPeriodStart}-${i}`,
      date: sessionDate.toISOString().split('T')[0],
      time: times[Math.floor(Math.random() * times.length)],
      client: clients[Math.floor(Math.random() * clients.length)],
      provider: providers[Math.floor(Math.random() * providers.length)],
      cptCode: cptCodes[Math.floor(Math.random() * cptCodes.length)],
      duration: durations[Math.floor(Math.random() * durations.length)],
      rateType: 'Licensed',
      amount: amounts[Math.floor(Math.random() * amounts.length)],
    });
  }
  sessions.sort((a, b) => a.date.localeCompare(b.date));
  return sessions;
}

const ADJUSTMENT_REASONS = ['Session cancelled', 'Duplicate payment', 'Client no-show'];
function generateMockAdjustments(payPeriodStart: string): SessionLineItem[] {
  const count = 3 + Math.floor(Math.random() * 3); // 3-5 entries
  const sessions = generateMockSessions(payPeriodStart, count);
  return sessions.map((s, i) => ({
    ...s,
    id: `adj-${s.id}`,
    amount: -s.amount,
    reason: ADJUSTMENT_REASONS[i % ADJUSTMENT_REASONS.length],
  }));
}

export const MOCK_INVOICES: Invoice[] = [
  {
    id: 'inv-1',
    date: '2025-01-15',
    payPeriod: 'Jan 1 – Jan 15, 2025',
    amount: 28750,
    status: 'Paid',
    stripeId: 'in_1ABC4x2eZvKY...',
    bankAccount: 'Chase Business ••••4821',
    sessions: generateMockSessions('2025-01-01', 164),
    adjustments: generateMockAdjustments('2025-01-01'),
  },
  {
    id: 'inv-2',
    date: '2025-01-01',
    payPeriod: 'Dec 16 – Dec 31, 2024',
    amount: 31200,
    status: 'Paid',
    stripeId: 'in_1AAB3w1dYuJX...',
    bankAccount: 'Chase Business ••••4821',
    sessions: generateMockSessions('2024-12-16', 178),
    adjustments: generateMockAdjustments('2024-12-16'),
  },
  {
    id: 'inv-3',
    date: '2024-12-15',
    payPeriod: 'Dec 1 – Dec 15, 2024',
    amount: 26400,
    status: 'Paid',
    stripeId: 'in_199A2v0cXtIW...',
    bankAccount: 'Chase Business ••••4821',
    sessions: generateMockSessions('2024-12-01', 152),
    adjustments: generateMockAdjustments('2024-12-01'),
  },
  {
    id: 'inv-4',
    date: '2024-12-01',
    payPeriod: 'Nov 16 – Nov 30, 2024',
    amount: 29850,
    status: 'Paid',
    stripeId: 'in_198Z1u9bWsHV...',
    bankAccount: 'Chase Business ••••4821',
    sessions: generateMockSessions('2024-11-16', 170),
    adjustments: generateMockAdjustments('2024-11-16'),
  },
  {
    id: 'inv-5',
    date: '2024-11-15',
    payPeriod: 'Nov 1 – Nov 15, 2024',
    amount: 27600,
    status: 'Paid',
    stripeId: 'in_197Y0t8aVrGU...',
    bankAccount: 'Chase Business ••••4821',
    sessions: generateMockSessions('2024-11-01', 158),
    adjustments: generateMockAdjustments('2024-11-01'),
  },
];

export interface PaymentAdjustment {
  invoiceId: string;
  invoiceLabel: string;
  adjustments: SessionLineItem[];
}

export interface Payment {
  id: string;
  date: string;
  amount: number;
  status: 'Succeeded' | 'Processing' | 'Scheduled' | 'Failed';
  paymentMethod: string;
  stripePaymentIntentId: string;
  primaryInvoiceId: string;
  primaryInvoiceLabel: string;
  primaryAmount: number;
  invoiceAdjustments: PaymentAdjustment[];
}

/** Build mock adjustment line items for receipt (negative amounts, from other invoices). */
function mockAdjustmentLines(
  _invoiceLabel: string,
  count: number,
  baseId: string
): SessionLineItem[] {
  const clients = ['Smith, J.', 'Garcia, M.', 'Johnson, T.', 'Williams, R.'];
  const providers = ['Dr. Sarah Chen', 'Dr. Michael Ross'];
  const reasons = ['Session cancelled', 'Duplicate payment', 'Client no-show', 'Incorrectly billed'];
  const items: SessionLineItem[] = [];
  for (let i = 0; i < count; i++) {
    const amt = 125 + Math.floor(Math.random() * 4) * 25;
    items.push({
      id: `adj-${baseId}-${i}`,
      date: '2024-12-10',
      time: '10:00 AM',
      client: clients[i % clients.length],
      provider: providers[i % providers.length],
      cptCode: '90834',
      duration: 45,
      rateType: 'Licensed',
      amount: -amt,
      reason: reasons[i % reasons.length],
    });
  }
  return items;
}
export const MOCK_PAYMENTS: Payment[] = [
  {
    id: 'pay-1',
    date: '2025-01-15',
    amount: 26250,
    status: 'Succeeded',
    paymentMethod: 'Chase Business ••••4821',
    stripePaymentIntentId: 'pi_1ABC4x2eZvKY...',
    primaryInvoiceId: 'inv-1',
    primaryInvoiceLabel: 'Jan 1 – Jan 15, 2025',
    primaryAmount: 28750,
    invoiceAdjustments: [
      { invoiceId: 'inv-2', invoiceLabel: 'Dec 16 – Dec 31, 2024', adjustments: mockAdjustmentLines('Dec 16 – Dec 31, 2024', 2, 'pay1-inv2') },
      { invoiceId: 'inv-3', invoiceLabel: 'Dec 1 – Dec 15, 2024', adjustments: mockAdjustmentLines('Dec 1 – Dec 15, 2024', 4, 'pay1-inv3') },
    ],
  },
  {
    id: 'pay-2',
    date: '2025-01-01',
    amount: 30400,
    status: 'Succeeded',
    paymentMethod: 'Chase Business ••••4821',
    stripePaymentIntentId: 'pi_1AAB3w1dYuJX...',
    primaryInvoiceId: 'inv-2',
    primaryInvoiceLabel: 'Dec 16 – Dec 31, 2024',
    primaryAmount: 31200,
    invoiceAdjustments: [
      { invoiceId: 'inv-3', invoiceLabel: 'Dec 1 – Dec 15, 2024', adjustments: mockAdjustmentLines('Dec 1 – Dec 15, 2024', 3, 'pay2-inv3') },
    ],
  },
  {
    id: 'pay-3',
    date: '2024-12-15',
    amount: 26400,
    status: 'Succeeded',
    paymentMethod: 'Chase Business ••••4821',
    stripePaymentIntentId: 'pi_199A2v0cXtIW...',
    primaryInvoiceId: 'inv-3',
    primaryInvoiceLabel: 'Dec 1 – Dec 15, 2024',
    primaryAmount: 26400,
    invoiceAdjustments: [],
  },
  {
    id: 'pay-4',
    date: '2024-12-01',
    amount: 29100,
    status: 'Succeeded',
    paymentMethod: 'Chase Business ••••4821',
    stripePaymentIntentId: 'pi_198Z1u9bWsHV...',
    primaryInvoiceId: 'inv-4',
    primaryInvoiceLabel: 'Nov 16 – Nov 30, 2024',
    primaryAmount: 29850,
    invoiceAdjustments: [
      { invoiceId: 'inv-5', invoiceLabel: 'Nov 1 – Nov 15, 2024', adjustments: mockAdjustmentLines('Nov 1 – Nov 15, 2024', 2, 'pay4-inv5') },
    ],
  },
  {
    id: 'pay-5',
    date: '2024-11-15',
    amount: 27600,
    status: 'Succeeded',
    paymentMethod: 'Chase Business ••••4821',
    stripePaymentIntentId: 'pi_197Y0t8aVrGU...',
    primaryInvoiceId: 'inv-5',
    primaryInvoiceLabel: 'Nov 1 – Nov 15, 2024',
    primaryAmount: 27600,
    invoiceAdjustments: [],
  },
  {
    id: 'pay-6',
    date: '2025-01-31',
    amount: 29500,
    status: 'Scheduled',
    paymentMethod: 'Chase Business ••••4821',
    stripePaymentIntentId: 'pi_1ABC5y3fAwLZ...',
    primaryInvoiceId: 'inv-6',
    primaryInvoiceLabel: 'Jan 16 – Jan 31, 2025',
    primaryAmount: 29500,
    invoiceAdjustments: [],
  },
];

export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
}

export interface FinancesPageProps {
  loading?: boolean;
  headerRight?: ReactNode;
  title?: string;
  subtitle?: string;
}

export function FinancesPage({
  loading = false,
  headerRight,
  title = 'Finances',
  subtitle = 'Dashboard, invoices, payments, and receivables',
}: FinancesPageProps): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab =
    tabParam === TAB_PARAM_INVOICE ? 'ledger'
    : tabParam === TAB_PARAM_PAYMENTS ? 'ar'
    : 'dashboard';
  const setActiveTab = (value: string | null) => {
    const param =
      value === 'ledger' ? TAB_PARAM_INVOICE
      : value === 'ar' ? TAB_PARAM_PAYMENTS
      : TAB_PARAM_DASHBOARD;
    navigate({ pathname: location.pathname, search: `?tab=${param}` });
  };
  const [trendGranularity, setTrendGranularity] = useState<string>('monthly');
  const PAGE_SIZE = 12;
  const [invoicePage, setInvoicePage] = useState(1);
  const [paymentPage, setPaymentPage] = useState(1);

  const invoiceTotalPages = Math.max(1, Math.ceil(MOCK_INVOICES.length / PAGE_SIZE));
  const paymentTotalPages = Math.max(1, Math.ceil(MOCK_PAYMENTS.length / PAGE_SIZE));
  const paginatedInvoices = MOCK_INVOICES.slice((invoicePage - 1) * PAGE_SIZE, invoicePage * PAGE_SIZE);
  const paginatedPayments = MOCK_PAYMENTS.slice((paymentPage - 1) * PAGE_SIZE, paymentPage * PAGE_SIZE);

  const trendData = trendGranularity === 'weekly' ? WEEKLY_TREND_DATA : MONTHLY_TREND_DATA;

  const revenueThisMonth = 54700;
  const revenueLastMonth = 52100;
  const growthPct = ((revenueThisMonth - revenueLastMonth) / revenueLastMonth) * 100;
  const upcomingPayments = MOCK_PAYMENTS.filter((p) => p.status === 'Scheduled' || p.status === 'Processing');
  const upcomingPaymentsTotal = upcomingPayments.reduce((sum, p) => sum + p.amount, 0);
  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <Group justify="space-between" align="flex-start">
          <div>
            <Group gap="xs" align="center">
              <Title order={2}>{title}</Title>
              <LocalhostOnlyBadge />
            </Group>
            <Text size="sm" c="gray.6" mt={4}>
              {subtitle}
            </Text>
          </div>
          {headerRight}
        </Group>

        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value="dashboard" leftSection={<IconTrendingUp size={16} />}>
              Dashboard
            </Tabs.Tab>
            <Tabs.Tab value="ledger" leftSection={<IconReceipt size={16} />}>
              Invoices
            </Tabs.Tab>
            <Tabs.Tab value="ar" leftSection={<IconCreditCard size={16} />}>
              Payments
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="dashboard" pt="xl">
            <Stack gap="lg">
              <Group grow align="stretch">
                <Card shadow="xs" withBorder padding="lg" radius="md" style={{ flex: '1 1 50%' }}>
                  <Group justify="space-between" mb="xs">
                    <Text size="sm" c="gray.6">Revenue this month</Text>
                    <IconCash size={20} style={{ color: 'var(--mantine-color-green-6)' }} />
                  </Group>
                  <Skeleton visible={loading} width="60%" height={28} mt={4}>
                    <Text size="xl" fw={700}>{formatCurrency(revenueThisMonth)}</Text>
                  </Skeleton>
                  <Skeleton visible={loading} width="45%" height={16} mt="xs">
                    <Group gap={4}>
                      <IconArrowUpRight size={14} style={{ color: 'var(--mantine-color-green-6)' }} />
                      <Text size="sm" c="green.6" fw={500}>{growthPct.toFixed(1)}% vs last month</Text>
                    </Group>
                  </Skeleton>
                </Card>
                <Card shadow="xs" withBorder padding="lg" radius="md" style={{ flex: '1 1 50%' }}>
                  <Group justify="space-between" mb="xs">
                    <Text size="sm" c="gray.6">Upcoming payments</Text>
                    <IconWallet size={20} style={{ color: 'var(--mantine-color-blue-6)' }} />
                  </Group>
                  <Skeleton visible={loading} width="50%" height={28} mt={4}>
                    <Text size="xl" fw={700}>{formatCurrency(upcomingPaymentsTotal)}</Text>
                  </Skeleton>
                  <Skeleton visible={loading} width="30%" height={16} mt="xs">
                    <Text size="sm" c="gray.6">{upcomingPayments.length} payment{upcomingPayments.length !== 1 ? 's' : ''}</Text>
                  </Skeleton>
                </Card>
              </Group>

              <ChartCard
                title="Revenue trend (last 6 months)"
                headerRight={
                  <SegmentedControl
                    size="xs"
                    value={trendGranularity}
                    onChange={setTrendGranularity}
                    data={[
                      { label: 'Monthly', value: 'monthly' },
                      { label: 'Weekly', value: 'weekly' },
                    ]}
                  />
                }
              >
                <Skeleton visible={loading} height={280}>
                  <UnifyLineChart
                    h={280}
                    data={trendData}
                    dataKey="period"
                    series={[{ name: 'revenue', color: 'green.6', label: 'Revenue' }]}
                    curveType="monotone"
                    withDots={trendGranularity === 'monthly'}
                    gridAxis="xy"
                    valueFormatter={(v) => formatCurrency(Number(v))}
                  />
                </Skeleton>
              </ChartCard>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="ledger" pt="xl">
            <Paper withBorder p="lg" radius="md">
              <Group justify="space-between" mb="md">
                <div>
                  <Title order={4}>Invoices</Title>
                  <Text size="sm" c="gray.6">Practice invoices by pay period</Text>
                </div>
              </Group>
              <Table highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Pay Period</Table.Th>
                    <Table.Th>Amount</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Stripe ID</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {loading
                    ? Array.from({ length: 5 }).map((_, i) => (
                        <Table.Tr key={i}>
                          {Array.from({ length: 4 }).map((__, j) => (
                            <Table.Td key={j}><Skeleton height={14} /></Table.Td>
                          ))}
                        </Table.Tr>
                      ))
                    : paginatedInvoices.map((inv) => (
                        <Table.Tr
                          key={inv.id}
                          style={{ cursor: 'pointer' }}
                          onClick={() => navigate(`${location.pathname}/invoice/${inv.id}`)}
                        >
                          <Table.Td>{inv.payPeriod}</Table.Td>
                          <Table.Td>{formatCurrency(inv.amount)}</Table.Td>
                          <Table.Td>
                            <Badge
                              size="sm"
                              variant="light"
                              color={inv.status === 'Paid' ? 'green' : inv.status === 'Pending' ? 'yellow' : 'red'}
                            >
                              {inv.status}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            <Text size="xs" ff="monospace" c="gray.6">{inv.stripeId}</Text>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                </Table.Tbody>
              </Table>
              <Group justify="center" mt="md">
                <Pagination
                  total={invoiceTotalPages}
                  value={invoicePage}
                  onChange={setInvoicePage}
                  size="sm"
                />
              </Group>
            </Paper>
          </Tabs.Panel>

          <Tabs.Panel value="ar" pt="xl">
            <Paper withBorder p="lg" radius="md">
              <Group justify="space-between" mb="md">
                <div>
                  <Title order={4}>Payments</Title>
                  <Text size="sm" c="gray.6">Payment history and upcoming payments</Text>
                </div>
                <Skeleton visible={loading} width={160} height={20}>
                  <Text fw={600}>Upcoming: {formatCurrency(upcomingPaymentsTotal)}</Text>
                </Skeleton>
              </Group>
              <Table highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Payment Date</Table.Th>
                    <Table.Th>Invoice</Table.Th>
                    <Table.Th>Amount</Table.Th>
                    <Table.Th>Payment Method</Table.Th>
                    <Table.Th>Status</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {loading
                    ? Array.from({ length: 5 }).map((_, i) => (
                        <Table.Tr key={i}>
                          {Array.from({ length: 5 }).map((__, j) => (
                            <Table.Td key={j}><Skeleton height={14} /></Table.Td>
                          ))}
                        </Table.Tr>
                      ))
                    : paginatedPayments.map((payment) => (
                        <Table.Tr
                          key={payment.id}
                          style={{ cursor: 'pointer' }}
                          onClick={() => navigate(`${location.pathname}/payment/${payment.id}`)}
                        >
                          <Table.Td>{payment.date}</Table.Td>
                          <Table.Td>{payment.primaryInvoiceLabel}</Table.Td>
                          <Table.Td>{formatCurrency(payment.amount)}</Table.Td>
                          <Table.Td>{payment.paymentMethod}</Table.Td>
                          <Table.Td>
                            <Badge
                              size="sm"
                              variant="light"
                              color={
                                payment.status === 'Succeeded' ? 'green'
                                  : payment.status === 'Processing' ? 'blue'
                                  : payment.status === 'Scheduled' ? 'gray'
                                  : 'red'
                              }
                            >
                              {payment.status}
                            </Badge>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                </Table.Tbody>
              </Table>
              <Group justify="center" mt="md">
                <Pagination
                  total={paymentTotalPages}
                  value={paymentPage}
                  onChange={setPaymentPage}
                  size="sm"
                />
              </Group>
            </Paper>
          </Tabs.Panel>

        </Tabs>
      </Stack>
    </Container>
  );
}
